import React, { useState, useMemo } from 'react';
import { Paintbrush, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { usePreferences } from '../../../context/PreferencesContext';
import ThemeSwatchRow from './ThemeSwatchRow';
import useThemeGroups from './useThemeGroups';

/** Category-grouped theme list; clicking a row picks it and closes. */
const ThemePickerPopup = ({ groups, value, onPick, onClose, scopeIsGlobal }) => (
  <div className="stui-modal-overlay">
    <div className="stui-modal stui-modal-md" role="dialog" aria-label="Pick a theme">
      <div className="stui-modal-header">
        <h3>Pick a Theme</h3>
        <button type="button" className="stui-modal-close" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="stui-modal-body">
        {groups.map(g => (
          <React.Fragment key={g.category}>
            <div className="stui-theme-subhead">{g.category}</div>
            {g.themes.map(t => (
              <div
                key={t.themeKey}
                role="button"
                tabIndex={0}
                aria-pressed={t.themeKey === value}
                className={`stui-theme-list-item${t.themeKey === value ? ' active' : ''}`}
                onClick={() => onPick(t.themeKey)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(t.themeKey); } }}
              >
                <ThemeSwatchRow tokens={t.tokens} />
                <span className="stui-theme-list-label">{t.label}</span>
                {/* Standard for All Databases is stored as "no theme of my own" */}
                {scopeIsGlobal && t.themeKey === 'standard' && (
                  <span className="stui-sublabel">Databases with their own default keep it</span>
                )}
                {t.status === 'Draft' && <span className="stui-badge-orange">Draft</span>}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="stui-modal-footer">
        <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>
);

/**
 * Display > Theme. Shows the theme in effect; with the page pencil on, the paintbrush opens a
 * category-grouped list beside the page's Apply to scope picker. Picking a theme tries it on the
 * whole app at once (the footer chip keeps it while browsing); Save or the footer Apply keeps it,
 * Cancel or Stop puts the saved one back.
 */
const ThemePickerRow = ({ isEditing, value, onPick, scopeControl, scopeIsGlobal, error }) => {
  const { isSysAdmin } = useAuth();
  const { themes } = usePreferences();
  const [open, setOpen] = useState(false);
  // Active themes for everyone; Drafts only reach (and only show for) SysAdmins.
  const choices = useMemo(
    () => themes.filter(t => t.status === 'Active' || (isSysAdmin && t.status === 'Draft')),
    [themes, isSysAdmin],
  );
  const { groups } = useThemeGroups(choices);
  const current = themes.find(t => t.themeKey === value) || themes.find(t => t.themeKey === 'standard') || null;

  const pick = (key) => { setOpen(false); onPick(key); };

  return (
    <div className="stui-row-setting">
      <div className="stui-row-setting-label">
        <div className="stui-label">Theme</div>
        <div className="stui-sublabel">
          {isEditing ? 'Pick a theme to try it across the app. Save or the footer Apply keeps it.' : 'Colors and styling for the whole app.'}
        </div>
        {error && <div className="stui-sublabel stui-text-error">{error}</div>}
      </div>
      <div className="stui-row-setting-controls">
        {current ? (
          <span className="stui-row-8">
            <ThemeSwatchRow tokens={current.tokens} />
            <span>{current.label}</span>
          </span>
        ) : <span className="stui-sublabel">Loading themes...</span>}
        {isEditing && (
          <>
            <button type="button" className="stui-crud-btn" title="Pick a theme" onClick={() => setOpen(true)}>
              <Paintbrush size={14} />
            </button>
            {scopeControl}
          </>
        )}
      </div>
      {open && (
        <ThemePickerPopup groups={groups} value={current?.themeKey} onPick={pick} onClose={() => setOpen(false)} scopeIsGlobal={scopeIsGlobal} />
      )}
    </div>
  );
};

export default ThemePickerRow;
